import React from 'react';
import get from 'lodash.get';
import { createSelector } from 'src/universal/redux/utils';
import textRemoveTags from 'service_modules/utils/text-remove-tags';
import { getSelectedCompareCards } from 'src/universal/redux/compareCardsTray';
import {
  getPageState,
  getPageData,
  getPageEep,
  getPznOfferEligibleDtm,
  getPageCardsData,
  getPageAllowedLists,
  getCardsByShortName
} from 'src/universal/redux/page';
import { getSpecialOfferList } from 'src/universal/redux/pznData.js';
import {
  getPznOfferList,
  getPznOfferByShortName,
  hasPznDataArrived
} from 'src/universal/redux/pznData.js';
import { getCardMemberCards } from 'src/universal/redux/cardMemberInfo';
import {
  pushContent,
  getAnnualFee,
  getWelcomeOfferSection,
  getAprSection,
  getWhatToEarnRow,
  getHowToEarnRow,
  getBenefitsSection
} from '../components/CompareTable/utils';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import {
  buildModalDataList,
  getFormattedCtaLinks,
  makePznEligibleDtm
} from 'page_modules/compare-cards/utils';
import { getCardColorInfoList } from 'src/universal/redux/cardColors';
import needPznEligibleEvent from 'src/universal/utils/needPznEligibleEvent';
import {
  Element,
  Color,
  FontType,
  Typography
} from '@americanexpress/aqx-components';
import { parseFAQ } from '../utils/index';
import { getCardListData } from 'src/universal/redux/compareCardsTray.js';
import {
  capitalizeButtonText,
  formatSpecialOfferButtonText
} from 'src/universal/utils/formatOfferButton';

export const getCardSelectorCards = createSelector(
  [getPageData, getCardsByShortName, getSelectedCompareCards],
  (pageData, cards, selectedCards) => {
    return pageData.compareCardList.content.map(category => {
      return {
        ...category,
        cards: category.cards.map(shortName => {
          const card = cards[shortName];
          const plainName = textRemoveTags(card.cardHeadline);
          return {
            imgSrc: card.appAssets.cardArt,
            imgAlt: '',
            cardName: card.cardHeadline,
            shortName,
            isSelected: selectedCards.includes(shortName),
            annualFee:
              card?.annualMemberShipFee?.fee.indexOf('$0') > -1
                ? 'No Annual Fee'
                : card?.annualMemberShipFee?.fee,
            ariaLabelAddCard: `Add ${plainName}`,
            ariaLabelRemoveCard: `Remove ${plainName}`
          };
        })
      };
    });
  }
);

export const getCardSlots = createSelector(
  [
    getCardListData,
    getPageEep(),
    getPznOfferList,
    getPageData,
    getCardColorInfoList
  ],
  (selectedCards, pgEep, offerList, pageData, colorInfoList) => {
    const dtmEvents = parseArrayToObject(pageData.dtmEvents, 'key');

    while (selectedCards.length < 3) {
      selectedCards.push(null);
    }
    const enhancedList = (selectedCards || []).map(card => {
      const colorInfo = colorInfoList.find(c => c.shortName === card?.shortName);
      const pznOffer = card
        ? (offerList || []).find(c => c.shortName === card.shortName) || {}
        : {};
      return card
        ? {
            ...card,
            pznOffer,
            links: parseArrayToObject(
              getFormattedCtaLinks({
                originalLinks: card.links,
                pageEep: pgEep,
                pznOffer: pznOffer,
                dtmEvents: dtmEvents,
                productName: card.dtmProductName,
                position: 'Bottom',
                colorInfo: colorInfo
              }),
              'key'
            )
          }
        : null;
    });
    return enhancedList;
  }
);

export const getCardListDataWithPznOffers = createSelector(
  [getCardListData, getPznOfferList],
  (selectedCards, offerList) => {
    const enhancedList = (selectedCards || []).map(card => {
      const pznOffer = card
        ? (offerList || []).find(c => c.shortName === card.shortName) || {}
        : {};
      return card ? { ...card, pznOffer } : null;
    });
    return enhancedList;
  }
);

export const getAddSupCardData = createSelector(
  getPageData,
  pageData => (pageData || {}).addSupUser
);

export const getUserCardList = createSelector(
  getCardMemberCards,
  userCards => userCards || []
);

export const getUpgradeDescription = createSelector(getPageState, pageState => {
  const labelItem = (get(pageState, 'page.labels') || []).find(
    item => item.identifier === 'upgradeCtaDescription'
  );
  return labelItem ? labelItem.text || '' : '';
});

export const getCardOverview = createSelector(
  getPageState,
  pageState => pageState.page.cardOverview || {}
);

const getCardOverviewSections = createSelector(
  getCardOverview,
  cardOverview => cardOverview.sections || []
);

export const getCardOverviewHeaders = createSelector(
  [getCardOverviewSections, getCardSelectorCards],
  (cardOverviewSections, selectedCardList) => {
    return cardOverviewSections.map(section => {
      return pushContent(
        <Element className="compare_CardOverviewHeaders">
          <Typography Component="h3" $size={20}>
            {section.header}
          </Typography>
          {selectedCardList.length > 0 &&
            section.description && (
              <Typography
                Component="h3"
                $family={FontType.HelveticaRegular}
                theme={{ paddingTop: 15, paddingBottom: 15, color: Color.Gray06 }}
              >
                {section.description}
              </Typography>
            )}
        </Element>,
        '30%'
      );
    });
  }
);

/* eslint-disable max-params */
export const getCardOverviewRows = createSelector(
  [
    getCardSlots,
    getCardOverviewSections,
    getPznOfferList,
    getPznOfferEligibleDtm,
    getSpecialOfferList,
    getCardColorInfoList,
    getPageAllowedLists,
    (_, param) => param
  ],
  (
    selectedCards,
    cardOverviewSections,
    pznOfferList,
    pznEligibleDtm,
    specialOffers,
    colorInfoList,
    allowedlists,
    param
  ) => {
    const { currentPermutations, hasOverviewRibbon } = param;
    const filteredCards = currentPermutations
      ? [
          selectedCards[currentPermutations[0] - 1],
          selectedCards[currentPermutations[1] - 1]
        ]
      : selectedCards;
    const headers = parseArrayToObject(cardOverviewSections, 'key');
    const rows = cardOverviewSections.map(() => []);
    filteredCards.map((card, index) => {
      let cardBadge = null;
      const isCardInList =
        (get(allowedlists, 'tier1Offers') || []).indexOf(card?.shortName) > -1;
      const cardCopy = card ? { ...card, cardBadge } : card;
      const pznEligibleDtmResult = cardCopy
        ? {
            needEvent: needPznEligibleEvent(pznOfferList, cardCopy.shortName),
            dtm: makePznEligibleDtm(
              pznEligibleDtm,
              cardCopy.shortName,
              specialOffers
            )
          }
        : { needEvent: false, dtm: {} };
      rows[0].push(getAnnualFee(card, colorInfoList));
      rows[1].push(
        getWelcomeOfferSection({
          card: cardCopy,
          pznOfferList: pznOfferList,
          pznEligibleDtmResult,
          isTier1CardAlready: isCardInList,
          hasOverviewRibbon
        })
      );
      rows[2].push(getAprSection(card, pznOfferList));
      rows[3].push(getWhatToEarnRow(card));
      rows[4].push(getHowToEarnRow(card, headers['howToEarn'], index, false));
    });
    return rows;
  }
);

export const getHighlightsData = createSelector(
  getPageState,
  pageState => pageState.page.highlights || {}
);

export const getAllBenefitsData = createSelector(
  [getCardSlots, getHighlightsData, (_, params) => params || {}],
  (selectedCards, highlights, { currentPermutations, isMobile }) => {
    return getBenefitsSection(
      selectedCards,
      highlights,
      currentPermutations,
      isMobile
    );
  }
);

export const getApplyModalData = createSelector(
  [getCardListData, getPageEep()],
  (cardList, pgEep) => {
    return buildModalDataList(cardList, pgEep);
  }
);

export const getIsPreselected = createSelector([getPageData], pageData => {
  return pageData.isPreselected;
});

export const getOpenFAQ = createSelector([getPageData], pageData => {
  return pageData?.openFAQ
    ? parseFAQ(Object.assign({}, pageData.openFAQ, { name: 'span' }))
    : undefined;
});

export const getCompareDisclaimer = createSelector([getPageData], pageData => {
  return pageData?.highlights?.disclaimer || '';
});

export const getCardsBeingCompared = createSelector(
  [getSelectedCompareCards, (_, currentPermutations) => currentPermutations],
  (selectedCards, currentPermutations) => {
    const allCardsBeingCompared = selectedCards.reduce(
      (acc, curr, i) => {
        acc[i] = curr;
        return acc;
      },
      [null, null, null]
    );

    const cardsBeingCompared = [
      allCardsBeingCompared[currentPermutations[0] - 1],
      allCardsBeingCompared[currentPermutations[1] - 1]
    ];
    return cardsBeingCompared;
  }
);

export const getCardsBeingComparedData = createSelector(
  [getCardsBeingCompared, getPageCardsData],
  (cardsBeingCompared, allCardsData) => {
    const cardsDataByShortName = parseArrayToObject(allCardsData, 'shortName');
    return cardsBeingCompared.map(name => cardsDataByShortName[name]);
  }
);

export const getCardsBeingComparedImages = createSelector(
  [getCardsBeingComparedData],
  cards => {
    return cards.map(card => ({
      cardArt: card?.appAssets?.cardArt,
      altText: card?.cardArtAltText,
      learnMoreSrc: card?.learnMore?.url,
      shortName: card?.shortName
    }));
  }
);

export const getOfferBadgeText = createSelector(
  [getPznOfferList, (_, shortName) => shortName],
  (pznOfferList, shortName) => {
    const pznOffer = pznOfferList.find(c => c.shortName === shortName) || {};
    const offerButtonText = pznOffer?.offerButton ?? '';
    return capitalizeButtonText(formatSpecialOfferButtonText(offerButtonText));
  }
);

export const getIsTier1Card = shortName =>
  createSelector([getPageAllowedLists], allowedlists => {
    const isCardInList = (allowedlists.tier1Offers || []).indexOf(shortName) > -1;
    return isCardInList;
  });

export const getIsTier2Offer = shortName =>
  createSelector([getPznOfferByShortName(shortName)], pznOffer => {
    function isTier2Offer(offerButton) {
      const regex = /welcome|extra value/i;
      const offerButtonIncluesSpecial =
        offerButton.includes('special') || offerButton.includes('Special');
      const offerButtonContainsWelcomeOrExtraValue = regex.test(offerButton);
      return (
        offerButton !== '' &&
        (!offerButtonContainsWelcomeOrExtraValue || offerButtonIncluesSpecial)
      );
    }
    return isTier2Offer(pznOffer?.offerButton ?? '');
  });

export const compareHasOverviewRibbon = createSelector(
  [state => state, hasPznDataArrived, (_, cardList) => cardList],
  (stateObj, hasPznArrived, cardList) => {
    /*
      DE583801: Why this selector?
      (1) Because the MatchHeight never worked inside <table> dom.
      (2) We must have a parent level CSS solution to acheive required alignment.
      (3) DE598360 4/24/2024: just relocated this selector from the original location of
          'src/universal/components/Experiments/G141G146/redux.js'
          because the original location prevents reusing of the existing selectors that 
          the current fix requires.
    */

    if (!hasPznArrived) {
      return false;
    }

    const resultCard = cardList.find(userCard => {
      if (!userCard) return false;
      const isTier1Card = getIsTier1Card(userCard.shortName)(stateObj);
      const isTier2Offer = getIsTier2Offer(userCard.shortName)(stateObj);
      return !isTier1Card && isTier2Offer;
    });

    return !!resultCard;
  }
);
